import { memo, useEffect, useRef, useState } from 'react';

import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  DayPart,
  PlanningEntityIdentifier,
  PlanningEntityType,
  TransportationMode
} from 'generated';

import { formatSecondsToHoursMinutes } from 'utils/date';

import { Box, styled } from '@mui/material';

import DropIndicator from '../../components/DropIndicator/DropIndicator';
import {
  panelThemeColors,
  PanelTheme
} from '../../components/ActivityPanel/constants';
import { StyledExtraSmallIcon } from 'components/shared/Icon/Icon.styles';

const StyledTravelTime = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 0;
  margin: -20px 0;
  width: 100%;
  min-height: 70px;
  position: relative;
  z-index: 1;
` as typeof Box;

const TextContainer = styled(Box)`
  display: flex;
  align-items: center;
  padding: 2px 8px;
  white-space: nowrap;
  z-index: 10;
  font-size: 12px;
`;

interface TravelTimePragmaticProps {
  id: string;
  dayPart: DayPart;
  scheduleStart: string;
  start: string;
  durationInSeconds: number;
  droppable: boolean | undefined;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier: PlanningEntityIdentifier;
  transportationMode?: TransportationMode;
}

function TravelTimePragmatic({
  id: _id,
  dayPart = 'MORNING',
  droppable,
  scheduleStart,
  start,
  durationInSeconds,
  planningEntityType,
  planningEntityId,
  planningEntityIdentifier,
  transportationMode = 'CAR'
}: TravelTimePragmaticProps) {
  const ref = useRef<HTMLLIElement>(null);
  const [isOver, setIsOver] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !droppable) return;

    return dropTargetForElements({
      element: el,
      getData: () => ({
        scheduleStart,
        start,
        planningEntityId,
        planningEntityType,
        planningEntityIdentifier
      }),
      onDragEnter: () => setIsOver(true),
      onDragLeave: () => setIsOver(false),
      onDrop: () => setIsOver(false)
    });
  }, [
    droppable,
    scheduleStart,
    start,
    planningEntityId,
    planningEntityType,
    planningEntityIdentifier
  ]);

  const { textColor } = panelThemeColors[dayPart as PanelTheme];
  const isCarRoute = transportationMode === 'CAR';

  return (
    <StyledTravelTime
      component="li"
      ref={ref}
      className="traveltime-container"
      my={1}
      sx={
        !droppable
          ? {
              // Non-droppable traveltimes register no drop target but still
              // visually overlap an adjacent FreeTime via negative margins.
              // Make the <li> transparent to hit-testing during a drag so the
              // FreeTime sibling beneath becomes the hit target and lights up.
              '[data-drag-active] &': { pointerEvents: 'none' }
            }
          : undefined
      }
    >
      {isOver ? (
        <DropIndicator />
      ) : (
        <TextContainer color={textColor}>
          <StyledExtraSmallIcon
            type={isCarRoute ? 'car-side' : 'bicycle'}
            sx={{
              fontSize: isCarRoute ? '12px' : '16px',
              marginRight: '8px'
            }}
          />
          {`${formatSecondsToHoursMinutes(durationInSeconds)} reistijd`}
        </TextContainer>
      )}
    </StyledTravelTime>
  );
}

export default memo(TravelTimePragmatic);
